<section class="page">
  <header class="page-header">
    <div>
      <h1>Advanced PM</h1>
      <p>Versions, releases, custom fields, and JQL-like issue search.</p>
    </div>
    <mat-form-field appearance="outline">
      <mat-label>Project</mat-label>
      <mat-select [value]="selectedProjectId()" (selectionChange)="onProjectChange($event.value)">
        @for (p of projects(); track p.id) {
          <mat-option [value]="p.id">{{ p.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
  </header>

  @if (error()) {
    <div class="error">{{ error() }}</div>
  }

  @if (loading()) {
    <div class="loading"><mat-spinner diameter="36"></mat-spinner></div>
  }

  <mat-tab-group [(selectedIndex)]="selectedTabIndex">
    <mat-tab label="Versions">
      <div class="tab-body">
        <div class="form-row">
          <mat-form-field appearance="outline"><mat-label>Name</mat-label><input matInput [(ngModel)]="versionName"></mat-form-field>
          <mat-form-field appearance="outline"><mat-label>Start date</mat-label><input matInput type="date" [(ngModel)]="versionStartDate"></mat-form-field>
          <mat-form-field appearance="outline"><mat-label>Release date</mat-label><input matInput type="date" [(ngModel)]="versionReleaseDate"></mat-form-field>
          <mat-form-field appearance="outline" class="grow"><mat-label>Description</mat-label><input matInput [(ngModel)]="versionDescription"></mat-form-field>
          <button mat-flat-button color="primary" type="button" (click)="createVersion()">Add version</button>
        </div>
        <table mat-table [dataSource]="versions()" class="full">
          <ng-container matColumnDef="name"><th mat-header-cell *matHeaderCellDef>Name</th><td mat-cell *matCellDef="let v">{{ v.name }}</td></ng-container>
          <ng-container matColumnDef="dates">
            <th mat-header-cell *matHeaderCellDef>Dates</th>
            <td mat-cell *matCellDef="let v">
              <input type="date" [value]="v.startDate || ''" #startInput (change)="saveVersionDates(v, startInput.value, releaseInput.value)" />
              →
              <input type="date" [value]="v.releaseDate || ''" #releaseInput (change)="saveVersionDates(v, startInput.value, releaseInput.value)" />
            </td>
          </ng-container>
          <ng-container matColumnDef="status"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let v">{{ v.statusLabel }}</td></ng-container>
          <ng-container matColumnDef="approval">
            <th mat-header-cell *matHeaderCellDef>Approval</th>
            <td mat-cell *matCellDef="let v">{{ v.approvalStatusLabel || '—' }}</td>
          </ng-container>
          <ng-container matColumnDef="checklist">
            <th mat-header-cell *matHeaderCellDef>Checklist</th>
            <td mat-cell *matCellDef="let v">
              @if (v.checklistTotal != null) {
                {{ v.checklistDone ?? 0 }}/{{ v.checklistTotal }}
              } @else { — }
            </td>
          </ng-container>
          <ng-container matColumnDef="deployment">
            <th mat-header-cell *matHeaderCellDef>Deployment</th>
            <td mat-cell *matCellDef="let v">
              <mat-select [value]="v.deploymentStatus ?? 0" (selectionChange)="updateDeploymentStatus(v, $event.value)" class="deploy-select">
                @for (d of deploymentStatuses; track d.value) {
                  <mat-option [value]="d.value">{{ d.label }}</mat-option>
                }
              </mat-select>
            </td>
          </ng-container>
          <ng-container matColumnDef="progress">
            <th mat-header-cell *matHeaderCellDef>Issues</th>
            <td mat-cell *matCellDef="let v">
              {{ v.completedIssueCount ?? 0 }}/{{ v.issueCount }}
              @if (v.progressPercent != null) { · {{ v.progressPercent }}% }
            </td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let v">
              @if (editingVersionId === v.id) {
                <mat-form-field appearance="outline" class="notes-area">
                  <mat-label>Release notes</mat-label>
                  <textarea matInput rows="2" [(ngModel)]="versionReleaseNotes"></textarea>
                </mat-form-field>
                <button mat-button type="button" (click)="saveVersionNotes(v)">Save notes</button>
                <button mat-button type="button" (click)="editingVersionId = ''">Cancel</button>
              } @else {
                <button mat-button type="button" (click)="editVersionNotes(v)">Notes</button>
              }
              @if (v.status === 0) {
                <button mat-button type="button" (click)="releaseVersion(v)">Release</button>
              }
              <button mat-button type="button" (click)="approveVersion(v)">Approve</button>
              <button mat-button type="button" (click)="rejectVersion(v)">Reject</button>
              <button mat-button type="button" [class.active]="selectedVersionId() === v.id" (click)="selectVersionForChecklist(v)">Checklist</button>
              <button mat-button type="button" (click)="deployVersion(v)">Deploy</button>
              @if (v.releaseNotes) { <span class="muted" [title]="v.releaseNotes">📝</span> }
              <button mat-button color="warn" type="button" (click)="deleteVersion(v.id)">Delete</button>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="versionColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: versionColumns"></tr>
        </table>

        @if (selectedVersionId(); as vid) {
          <section class="checklist-panel">
            <h3>Release checklist</h3>
            @if (loadingChecklist()) {
              <mat-spinner diameter="24"></mat-spinner>
            } @else {
              <table mat-table [dataSource]="checklistItems()" class="full">
                <ng-container matColumnDef="done">
                  <th mat-header-cell *matHeaderCellDef>Done</th>
                  <td mat-cell *matCellDef="let item">
                    <mat-checkbox [checked]="item.isDone" (change)="toggleChecklistItem(item)"></mat-checkbox>
                  </td>
                </ng-container>
                <ng-container matColumnDef="title">
                  <th mat-header-cell *matHeaderCellDef>Item</th>
                  <td mat-cell *matCellDef="let item" [class.done]="item.isDone">{{ item.title }}</td>
                </ng-container>
                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef></th>
                  <td mat-cell *matCellDef="let item">
                    <button mat-icon-button type="button" (click)="deleteChecklistItem(item)"><mat-icon>delete</mat-icon></button>
                  </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="checklistColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: checklistColumns"></tr>
              </table>
              @if (!checklistItems().length) {
                <p class="muted">No checklist items yet.</p>
              }
              <div class="form-row">
                <mat-form-field appearance="outline" class="grow">
                  <mat-label>New checklist item</mat-label>
                  <input matInput [(ngModel)]="newChecklistTitle" (keyup.enter)="addChecklistItem()" />
                </mat-form-field>
                <button mat-stroked-button type="button" [disabled]="!newChecklistTitle.trim()" (click)="addChecklistItem()">Add</button>
              </div>
            }
          </section>
        }
      </div>
    </mat-tab>

    <mat-tab label="Milestones">
      <div class="tab-body">
        <div class="form-row">
          <mat-form-field appearance="outline"><mat-label>Name</mat-label><input matInput [(ngModel)]="milestoneName"></mat-form-field>
          <mat-form-field appearance="outline"><mat-label>Target date</mat-label><input matInput type="date" [(ngModel)]="milestoneTargetDate"></mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Version</mat-label>
            <mat-select [(ngModel)]="milestoneVersionId">
              <mat-option value="">None</mat-option>
              @for (v of versions(); track v.id) {
                <mat-option [value]="v.id">{{ v.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Status</mat-label>
            <mat-select [(ngModel)]="milestoneStatus">
              @for (s of milestoneStatuses; track s.value) {
                <mat-option [value]="s.value">{{ s.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="grow"><mat-label>Description</mat-label><input matInput [(ngModel)]="milestoneDescription"></mat-form-field>
          @if (editingMilestoneId) {
            <button mat-flat-button color="primary" type="button" (click)="saveMilestone()">Save milestone</button>
            <button mat-button type="button" (click)="resetMilestoneForm()">Cancel</button>
          } @else {
            <button mat-flat-button color="primary" type="button" (click)="createMilestone()">Add milestone</button>
          }
        </div>
        <table mat-table [dataSource]="milestones()" class="full">
          <ng-container matColumnDef="name"><th mat-header-cell *matHeaderCellDef>Name</th><td mat-cell *matCellDef="let m">{{ m.name }}</td></ng-container>
          <ng-container matColumnDef="targetDate"><th mat-header-cell *matHeaderCellDef>Target date</th><td mat-cell *matCellDef="let m">{{ m.targetDate || 'TBD' }}</td></ng-container>
          <ng-container matColumnDef="version"><th mat-header-cell *matHeaderCellDef>Version</th><td mat-cell *matCellDef="let m">{{ m.versionName || '—' }}</td></ng-container>
          <ng-container matColumnDef="status"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let m">{{ m.statusLabel || m.status }}</td></ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let m">
              <button mat-button type="button" (click)="editMilestone(m)">Edit</button>
              <button mat-button color="warn" type="button" (click)="deleteMilestone(m.id)">Delete</button>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="milestoneColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: milestoneColumns"></tr>
        </table>
        @if (!milestones().length) {
          <p class="muted">No milestones yet.</p>
        }
      </div>
    </mat-tab>

    <mat-tab label="Custom fields">
      <div class="tab-body">
        <div class="form-row">
          <mat-form-field appearance="outline"><mat-label>Name</mat-label><input matInput [(ngModel)]="fieldName"></mat-form-field>
          <mat-form-field appearance="outline"><mat-label>Key</mat-label><input matInput [(ngModel)]="fieldKey" placeholder="priority_score"></mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Type</mat-label>
            <mat-select [(ngModel)]="fieldType">
              @for (t of fieldTypes; track t.value) { <mat-option [value]="t.value">{{ t.label }}</mat-option> }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="grow"><mat-label>Options JSON</mat-label><input matInput [(ngModel)]="fieldOptions" placeholder='["A","B"]'></mat-form-field>
          <button mat-flat-button color="primary" type="button" (click)="createField()">Add field</button>
        </div>
        <ul class="plain-list">
          @for (f of customFields(); track f.id) {
            <li>
              <strong>{{ f.name }}</strong> <code>{{ f.key }}</code> · {{ f.fieldTypeLabel }}
              <button mat-button color="warn" type="button" (click)="deleteField(f.id)">Delete</button>
            </li>
          } @empty { <li class="muted">No custom fields yet.</li> }
        </ul>
      </div>
    </mat-tab>

    <mat-tab label="Advanced search">
      <div class="tab-body">
        <p class="hint">JQL-like: <code>status = Open AND priority = High AND text ~ login</code>. Fields: project, type, status, priority, assignee, component, label, fixVersion, sprint, key, epic, text, cf.key</p>
        @if (selectedFilterId) {
          <p class="filter-active">Using saved filter. <button mat-button type="button" (click)="clearSelectedFilter()">Clear</button></p>
        }
        <div class="jql-chips">
          <mat-chip-set>
            <mat-chip (click)="appendJqlChip('status = Open')">status = Open</mat-chip>
            <mat-chip (click)="appendJqlChip('priority = High')">priority = High</mat-chip>
            <mat-chip (click)="appendJqlChip('type = Bug')">type = Bug</mat-chip>
            <mat-chip (click)="appendJqlChip('assignee = currentUser()')">assignee = currentUser()</mat-chip>
            <mat-chip (click)="appendJqlChip('text ~ ')">text ~ </mat-chip>
          </mat-chip-set>
        </div>
        <div class="form-row">
          <mat-form-field appearance="outline" class="grow"><mat-label>JQL</mat-label><input matInput [(ngModel)]="jql" [disabled]="!!selectedFilterId"></mat-form-field>
          <button mat-flat-button color="primary" type="button" (click)="searchPage = 0; runSearch()">Search</button>
        </div>
        <div class="form-row">
          <mat-form-field appearance="outline"><mat-label>Save as</mat-label><input matInput [(ngModel)]="filterName"></mat-form-field>
          <mat-checkbox [(ngModel)]="filterShared">Shared</mat-checkbox>
          @if (editingFilterId) {
            <button mat-stroked-button type="button" (click)="updateFilter()">Update filter</button>
            <button mat-button type="button" (click)="editingFilterId = ''; filterName = ''">Cancel edit</button>
          } @else {
            <button mat-stroked-button type="button" (click)="saveFilter()">Save filter</button>
          }
        </div>
        <div class="saved">
          @for (f of filters(); track f.id) {
            <button mat-stroked-button type="button" [class.active]="selectedFilterId === f.id" (click)="applyFilter(f)">{{ f.name }}</button>
            <button mat-icon-button type="button" matTooltip="Edit filter" (click)="startEditFilter(f)"><mat-icon>edit</mat-icon></button>
            <button mat-icon-button type="button" (click)="deleteFilter(f.id)"><mat-icon>delete</mat-icon></button>
          }
        </div>
        <p class="muted">{{ searchTotal() }} result(s)</p>
        <table mat-table [dataSource]="searchHits()" class="full">
          <ng-container matColumnDef="key"><th mat-header-cell *matHeaderCellDef>Key</th>
            <td mat-cell *matCellDef="let i"><a [routerLink]="['/issues', i.id]">{{ i.key }}</a></td></ng-container>
          <ng-container matColumnDef="summary"><th mat-header-cell *matHeaderCellDef>Summary</th><td mat-cell *matCellDef="let i">{{ i.summary }}</td></ng-container>
          <ng-container matColumnDef="typeLabel"><th mat-header-cell *matHeaderCellDef>Type</th><td mat-cell *matCellDef="let i">{{ i.typeLabel }}</td></ng-container>
          <ng-container matColumnDef="statusLabel"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let i">{{ i.statusLabel }}</td></ng-container>
          <ng-container matColumnDef="assigneeName"><th mat-header-cell *matHeaderCellDef>Assignee</th><td mat-cell *matCellDef="let i">{{ i.assigneeName || '—' }}</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="searchColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: searchColumns"></tr>
        </table>
        <mat-paginator
          [length]="searchTotal()"
          [pageIndex]="searchPage"
          [pageSize]="searchPageSize"
          [pageSizeOptions]="[10, 25, 50, 100]"
          (page)="onSearchPage($event)">
        </mat-paginator>
      </div>
    </mat-tab>

    <mat-tab label="Release calendar">
      <div class="tab-body">
        <div class="form-row">
          <mat-form-field appearance="outline"><mat-label>From</mat-label><input matInput type="date" [(ngModel)]="calendarFrom"></mat-form-field>
          <mat-form-field appearance="outline"><mat-label>To</mat-label><input matInput type="date" [(ngModel)]="calendarTo"></mat-form-field>
          <button mat-stroked-button type="button" (click)="loadReleaseCalendar()">Refresh calendar</button>
          <button mat-stroked-button type="button" [class.active]="calendarView === 'table'" (click)="calendarView = 'table'">Table</button>
          <button mat-stroked-button type="button" [class.active]="calendarView === 'month'" (click)="calendarView = 'month'">Month grid</button>
        </div>
        @if (loadingCalendar()) {
          <div class="loading"><mat-spinner diameter="28"></mat-spinner></div>
        } @else if (calendarView === 'month') {
          <div class="month-nav form-row">
            <button mat-icon-button type="button" (click)="shiftCalendarMonth(-1)"><mat-icon>chevron_left</mat-icon></button>
            <strong>{{ calendarMonthLabel() }}</strong>
            <button mat-icon-button type="button" (click)="shiftCalendarMonth(1)"><mat-icon>chevron_right</mat-icon></button>
          </div>
          <div class="month-grid">
            @for (dow of ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']; track dow) {
              <div class="month-dow">{{ dow }}</div>
            }
            @for (cell of calendarMonthCells(); track cell.key) {
              <div class="month-cell" [class.outside]="!cell.inMonth">
                <span class="day-num">{{ cell.day }}</span>
                @for (e of cell.entries; track e.id) {
                  <div class="month-event" [title]="e.name">{{ e.name }}</div>
                }
              </div>
            }
          </div>
          @if (!releaseCalendar().length) {
            <p class="muted">No releases in this range.</p>
          }
        } @else {
          <table mat-table [dataSource]="releaseCalendar()" class="full">
            <ng-container matColumnDef="name"><th mat-header-cell *matHeaderCellDef>Version</th><td mat-cell *matCellDef="let e">{{ e.name }}</td></ng-container>
            <ng-container matColumnDef="releaseDate"><th mat-header-cell *matHeaderCellDef>Release date</th><td mat-cell *matCellDef="let e">{{ e.releaseDate || 'TBD' }}</td></ng-container>
            <ng-container matColumnDef="status"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let e">{{ e.statusLabel || e.status }}</td></ng-container>
            <ng-container matColumnDef="deployment"><th mat-header-cell *matHeaderCellDef>Deployment</th><td mat-cell *matCellDef="let e">{{ deploymentLabel(e.deploymentStatus, e.deploymentStatusLabel) }}</td></ng-container>
            <ng-container matColumnDef="notes"><th mat-header-cell *matHeaderCellDef>Notes</th><td mat-cell *matCellDef="let e">{{ e.releaseNotes || '—' }}</td></ng-container>
            <tr mat-header-row *matHeaderRowDef="calendarColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: calendarColumns"></tr>
          </table>
          @if (!releaseCalendar().length) {
            <p class="muted">No releases in this range.</p>
          }
        }
      </div>
    </mat-tab>

    <mat-tab label="Timeline">
      <div class="tab-body">
        @if (selectedProjectId(); as pid) {
          <app-pm-timeline-view [projectId]="pid" [showShare]="true"></app-pm-timeline-view>
        } @else {
          <p class="muted">Select a project to view the roadmap timeline.</p>
        }
      </div>
    </mat-tab>

    <mat-tab label="Portfolio">
      <div class="tab-body portfolio-tab">
        <p class="hint">Cross-project roadmap and timeline grouped by account/portfolio.
          @if (portfolioSource() === 'accounts') {
            <span class="warn-text">Using accounts list (portfolio API not available).</span>
          }
        </p>
        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Portfolio / Account</mat-label>
            <mat-select [value]="selectedPortfolioId()" (selectionChange)="onPortfolioChange($event.value)">
              @for (pf of portfolios(); track pf.accountId) {
                <mat-option [value]="pf.accountId">
                  {{ pf.accountName }}
                  @if (pf.projectCount) { ({{ pf.projectCount }} projects) }
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button mat-stroked-button type="button" (click)="loadPortfolios(selectedPortfolioId())">
            <mat-icon>refresh</mat-icon> Refresh
          </button>
          @if (loadingPortfolios()) { <mat-spinner diameter="24"></mat-spinner> }
        </div>

        @if (selectedPortfolioId(); as pfId) {
          @for (pf of portfolios(); track pf.accountId) {
            @if (pf.accountId === pfId) {
              <div class="portfolio-stats">
                <span class="stat-chip" [ngClass]="healthRagClass(pf.healthLabel, pf.healthScore)">
                  {{ pf.healthLabel || 'Healthy' }} · score {{ pf.healthScore }}
                </span>
                <span class="stat-chip">{{ pf.completionPercent | number:'1.0-0' }}% complete</span>
                <span class="stat-chip">{{ pf.projectCount }} projects</span>
                <span class="stat-chip">{{ pf.overdueIssues }} overdue</span>
                <span class="stat-chip">{{ pf.blockedIssues }} blocked</span>
              </div>
            }
          }

          @if (loadingPortfolioRoadmap()) {
            <mat-spinner diameter="28"></mat-spinner>
          } @else if (portfolioRoadmap()) {
            <section class="portfolio-roadmap">
              <h3>Roadmap — {{ portfolioRoadmap()!.accountName }}</h3>
              @if (portfolioRoadmap()!.quarters.length) {
                @for (q of portfolioRoadmap()!.quarters; track q.quarter) {
                  <div class="quarter-block">
                    <h4>{{ q.quarter }}</h4>
                    @if (q.versions.length) {
                      <ul class="plain-list">
                        @for (v of q.versions; track v.id) {
                          <li>
                            <strong>{{ v.name }}</strong>
                            <span class="muted"> · {{ v.projectName }}</span>
                            @if (v.releaseDate) { <span class="muted"> · {{ v.releaseDate }}</span> }
                          </li>
                        }
                      </ul>
                    }
                    @if (q.milestones.length) {
                      <ul class="plain-list milestones">
                        @for (m of q.milestones; track m.id) {
                          <li>
                            <mat-icon class="inline-icon">flag</mat-icon>
                            {{ m.name }}
                            <span class="muted"> · {{ m.projectName }}</span>
                            @if (m.targetDate) { <span class="muted"> · {{ m.targetDate }}</span> }
                          </li>
                        }
                      </ul>
                    }
                  </div>
                }
              } @else if (portfolioRoadmap()!.versions.length || portfolioRoadmap()!.milestones.length) {
                @if (portfolioRoadmap()!.versions.length) {
                  <h4>Versions</h4>
                  <ul class="plain-list">
                    @for (v of portfolioRoadmap()!.versions; track v.id) {
                      <li><strong>{{ v.name }}</strong> · {{ v.projectName }} · {{ v.releaseDate || 'TBD' }}</li>
                    }
                  </ul>
                }
                @if (portfolioRoadmap()!.milestones.length) {
                  <h4>Milestones</h4>
                  <ul class="plain-list">
                    @for (m of portfolioRoadmap()!.milestones; track m.id) {
                      <li>{{ m.name }} · {{ m.projectName }} · {{ m.targetDate || 'TBD' }}</li>
                    }
                  </ul>
                }
              } @else {
                <p class="muted">No roadmap items for this portfolio yet.</p>
              }
            </section>
          }

          <section class="portfolio-timeline">
            <h3>Cross-project timeline</h3>
            <app-pm-timeline-view [portfolioId]="pfId" [showShare]="false"></app-pm-timeline-view>
          </section>
        } @else if (!loadingPortfolios()) {
          <p class="muted">No portfolios available.</p>
        }
      </div>
    </mat-tab>

    <mat-tab label="Components">
      <div class="tab-body">
        <div class="form-row">
          <mat-form-field appearance="outline"><mat-label>Name</mat-label><input matInput [(ngModel)]="componentName"></mat-form-field>
          <mat-form-field appearance="outline" class="grow"><mat-label>Description</mat-label><input matInput [(ngModel)]="componentDescription"></mat-form-field>
          <button mat-flat-button color="primary" type="button" [disabled]="!componentName.trim()" (click)="createComponent()">Add component</button>
          <button mat-stroked-button type="button" (click)="loadComponents()"><mat-icon>refresh</mat-icon> Refresh</button>
        </div>
        <table mat-table [dataSource]="components()" class="full">
          <ng-container matColumnDef="name"><th mat-header-cell *matHeaderCellDef>Name</th><td mat-cell *matCellDef="let c">{{ c.name }}</td></ng-container>
          <ng-container matColumnDef="description"><th mat-header-cell *matHeaderCellDef>Description</th><td mat-cell *matCellDef="let c">{{ c.description || '—' }}</td></ng-container>
          <ng-container matColumnDef="lead"><th mat-header-cell *matHeaderCellDef>Lead</th><td mat-cell *matCellDef="let c">{{ c.leadName || '—' }}</td></ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let c">
              <button mat-icon-button type="button" (click)="deleteComponent(c.id)" matTooltip="Delete"><mat-icon>delete</mat-icon></button>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="componentColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: componentColumns"></tr>
        </table>
        @if (!components().length) {
          <p class="muted">No components yet.</p>
        }
      </div>
    </mat-tab>
  </mat-tab-group>
</section>
